import { useAppDispatch, useAppSelector } from '../app/hooks';
import { toggleSidebar } from '../features/ui/uiSlice';
import { logOut } from '../features/auth/authSlice';
import { useLogoutMutation } from '../features/auth/authApi';
import { useNavigate, useLocation } from 'react-router-dom';
import { useState, useRef } from 'react';
import { useOutsideClick } from '../hooks/useOutsideClick';
import DashboardToggle from './Layout/DashboardToggle';
import { toggleViewMode } from '../features/auth/authSlice';
import { useGetSearchResultsQuery } from '../features/search/searchApi';


const Navbar = () => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const user = useAppSelector((state) => state.auth.user);
    const [logoutApi] = useLogoutMutation();
    const [searchQuery, setSearchQuery] = useState('');
    const { data: searchResults, isFetching: isSearching } = useGetSearchResultsQuery(searchQuery, {
        skip: searchQuery.length < 2,
    });

    const [isProfileOpen, setIsProfileOpen] = useState(false);
    const viewMode = useAppSelector((state) => state.auth.viewMode);
    const profileRef = useRef<HTMLDivElement>(null);
    const searchRef = useRef<HTMLDivElement>(null);

    useOutsideClick(profileRef, () => {
        if (isProfileOpen) setIsProfileOpen(false);
    });

    useOutsideClick(searchRef, () => {
        if (searchQuery) setSearchQuery('');
    });

    const handleProfileClick = () => {
        if (viewMode !== 'ESS') {
            dispatch(toggleViewMode());
        }
        navigate('/ess/profile');
        setIsProfileOpen(false);
    };

    const handleSettingsClick = () => {
        if (viewMode !== 'ADMIN') {
            dispatch(toggleViewMode());
        }
        navigate('/settings/global');
        setIsProfileOpen(false);
    };

    const handleLogout = async () => {
        try {
            await logoutApi().unwrap();
            dispatch(logOut());
            navigate('/login');
        } catch (error) {
            console.error('Logout failed', error);
            dispatch(logOut());
            navigate('/login');
        }
    };

    const getModuleName = () => {
        const path = location.pathname;
        if (path === '/dashboard' || path === '/') return 'Dashboard';
        if (path === '/employees') return 'People';
        if (path === '/leaves') return 'Time Off';
        if (path === '/departments') return 'Departments';
        if (path === '/my-info') return 'My Info';
        if (path === '/hiring') return 'Hiring';
        if (path === '/reports') return 'Reports';
        return 'HR Management';
    };

    return (
        <header className="flex items-center justify-between px-8 py-4 bg-white shadow-sm border-b border-gray-100 sticky top-0 z-40">
            <div className="flex items-center gap-6">
                <button
                    onClick={() => dispatch(toggleSidebar())}
                    className="text-gray-500 focus:outline-none lg:hidden hover:text-blue-600 transition-colors"
                >
                    <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M4 6H20M4 12H20M4 18H11" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                </button>
                <h1 className="text-xl font-bold text-gray-900 hidden sm:block tracking-tight">
                    {getModuleName()}
                </h1>
            </div>

            {/* Global Search */}
            <div className="flex-1 max-w-md px-4 hidden md:block" ref={searchRef}>
                <div className="relative group">
                    <span className="absolute inset-y-0 left-0 flex items-center pl-3">
                        <svg className={`w-5 h-5 transition-colors ${isSearching ? 'text-blue-500 animate-pulse' : 'text-gray-400 group-focus-within:text-blue-500'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                        </svg>
                    </span>
                    <input
                        type="text"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="block w-full pl-10 pr-3 py-2 border border-gray-200 rounded-lg leading-5 bg-gray-50 placeholder-gray-400 focus:outline-none focus:bg-white focus:ring-2 focus:ring-blue-100 focus:border-blue-400 sm:text-sm transition-all"
                        placeholder="Search employees, assets, depts..."
                    />

                    {/* Search Results Dropdown */}
                    {searchQuery.length >= 2 && (searchResults || isSearching) && (
                        <div className="absolute mt-2 w-full bg-white rounded-xl shadow-2xl border border-gray-100 py-2 z-50 animate-in fade-in slide-in-from-top-2 duration-200 divide-y divide-gray-50 max-h-96 overflow-y-auto">
                            {isSearching ? (
                                <div className="px-4 py-3 text-sm text-gray-500 flex items-center gap-2">
                                    <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
                                    Searching...
                                </div>
                            ) : searchResults?.length === 0 ? (
                                <div className="px-4 py-3 text-sm text-gray-500">No results found for "{searchQuery}"</div>
                            ) : (
                                searchResults?.map((result) => (
                                    <button
                                        key={`${result.type}-${result.id}`}
                                        onClick={() => {
                                            const path = result.type === 'employee' ? `/employees/${result.id}` : 
                                                         result.type === 'department' ? `/departments/${result.id}` : 
                                                         result.type === 'asset' ? `/assets` : '#';
                                            navigate(path);
                                            setSearchQuery('');
                                        }}
                                        className="w-full flex items-center gap-3 px-4 py-3 hover:bg-blue-50 transition-colors text-left"
                                    >
                                        <div className={`p-2 rounded-lg ${
                                            result.type === 'employee' ? 'bg-blue-100 text-blue-600' :
                                            result.type === 'department' ? 'bg-purple-100 text-purple-600' :
                                            result.type === 'asset' ? 'bg-orange-100 text-orange-600' :
                                            'bg-gray-100 text-gray-600'
                                        }`}>
                                            {result.type === 'employee' && <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg>}
                                            {result.type === 'department' && <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" /></svg>}
                                            {result.type === 'asset' && <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z" /></svg>}
                                        </div>
                                        <div>
                                            <div className="text-sm font-semibold text-gray-900">{result.title}</div>
                                            <div className="text-xs text-gray-500 lowercase first-letter:uppercase">{result.subtitle || result.type}</div>
                                        </div>
                                    </button>
                                ))
                            )}
                        </div>
                    )}
                </div>
            </div>

            <div className="flex items-center gap-4">
                <DashboardToggle />
                {user && (
                    <div className="relative" ref={profileRef}>
                        <button
                            onClick={() => setIsProfileOpen(!isProfileOpen)}
                            className="flex items-center gap-3 p-1 rounded-full hover:bg-gray-50 transition-colors focus:outline-none"
                        >
                            <div className="h-9 w-9 rounded-full bg-blue-600 flex items-center justify-center text-white font-bold text-sm border-2 border-white shadow-sm">
                                {user.firstName?.charAt(0)}{user.lastName?.charAt(0)}
                            </div>
                            <div className="hidden lg:flex flex-col items-start text-left pr-2">
                                <span className="text-sm font-semibold text-gray-900 leading-tight">
                                    {user.firstName} {user.lastName}
                                </span>
                                <span className="text-xs text-gray-500 font-medium">
                                    {user.role}
                                </span>
                            </div>
                            <svg className={`w-4 h-4 text-gray-400 transition-transform ${isProfileOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                        </button>

                        {/* Profile Dropdown */}
                        {isProfileOpen && (
                            <div className="absolute right-0 mt-2 w-56 bg-white rounded-xl shadow-xl border border-gray-100 py-2 z-50 animate-in fade-in slide-in-from-top-2 duration-200">
                                <div className="px-4 py-2 border-b border-gray-50 mb-1">
                                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Account</p>
                                    <p className="text-sm font-medium text-gray-900 truncate">{user.email}</p>
                                </div>
                                <button 
                                    onClick={handleProfileClick}
                                    className="w-full flex items-center px-4 py-2.5 text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-600 transition-colors"
                                >
                                    <svg className="w-4 h-4 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg>
                                    Your Profile
                                </button>
                                <button 
                                    onClick={handleSettingsClick}
                                    className="w-full flex items-center px-4 py-2.5 text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-600 transition-colors"
                                >
                                    <svg className="w-4 h-4 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37a1.724 1.724 0 002.572-1.065z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
                                    Settings
                                </button>
                                <div className="border-t border-gray-50 mt-1 pt-1">
                                    <button
                                        onClick={handleLogout}
                                        className="w-full flex items-center px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition-colors font-medium"
                                    >
                                        <svg className="w-4 h-4 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" /></svg>
                                        Logout
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </header>
    );
};

export default Navbar;
